import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRightIcon, PhoneIcon } from 'lucide-react';
import { LandingHero } from '../components/LandingHero';
import { TrustLocalBand } from '../components/TrustLocalBand';
import { FaqList } from '../components/FaqList';
import { categoryOptionValue } from '../components/HeroLeadForm';
import { NotFound } from './NotFound';
import { findCategory } from '../data/serviceCategories';
import { categoryContent } from '../data/categoryContent';
import { business } from '../data/site';
import { usePageMeta } from '../hooks/usePageMeta';
export function ServiceCategory() {
  const {
    categorySlug
  } = useParams();
  const category = findCategory(categorySlug);
  const content = category ? categoryContent[category.slug] : undefined;
  usePageMeta(category ? `${category.name} in Oak Ridge, TN | East End Automotive Repair` : 'Services | East End Automotive Repair', category && content ? `${content.valueStatement} Call East End Automotive Repair in Oak Ridge, TN at ${business.phone}.` : 'Auto repair services in Oak Ridge, TN from East End Automotive Repair.');
  if (!category || !content) return <NotFound data-id="element-660" data-mp="340fis6jvi2zr" />;
  return <>
      <LandingHero eyebrow="Oak Ridge, Tennessee" title={`${category.name} in Oak Ridge, TN`} intro={content.valueStatement} crumbs={[{
      label: 'Services',
      to: '/services'
    }, {
      label: category.name
    }]} defaultService={categoryOptionValue(category.slug)} sourcePage={`category-${category.slug}`} data-id="element-661" data-mp="33zcs9zqgq2kd" />

      <section className="w-full bg-white py-14 lg:py-20" data-id="element-662" data-mp="2l9efj6bpzlae">
        <div className="mx-auto max-w-shell px-5 lg:px-8" data-id="element-663" data-mp="2l7jwjxk0e38y">
          <div className="grid gap-12 lg:grid-cols-[minmax(0,1fr)_minmax(0,360px)] lg:gap-16" data-id="element-664" data-mp="2gc4knb5kahf6">
            <div data-id="element-665" data-mp="2bap2bqurqp0h">
              <h2 className="font-display text-[24px] font-extrabold leading-[1.1] tracking-[-0.02em] text-ink sm:text-[30px]" data-id="element-666" data-mp="2bb8f6t5kan7n">
                What {category.name} covers
              </h2>
              {content.overview.map(paragraph => <p key={paragraph.slice(0, 40)} className="mt-4 max-w-[640px] text-[16px] leading-relaxed text-steel/80" data-id="element-667" data-mp="21nuuhus495g7">
                  {paragraph}
                </p>)}

              <h3 className="mt-10 font-display text-[19px] font-bold tracking-tight text-ink" data-id="element-668" data-mp="1rqq6rqimh1xa">
                Situations that bring customers in
              </h3>
              <ul className="mt-4 space-y-2.5" data-id="element-669" data-mp="2kf5ert3c6jz4">
                {content.symptoms.map(symptom => <li key={symptom} className="flex items-start gap-2.5 text-[15.5px] leading-snug text-steel/80" data-id="element-670" data-mp="26o0oviprt61z">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand" aria-hidden="true" data-id="element-671" data-mp="2yeexp0ixfs6s" />
                    {symptom}
                  </li>)}
              </ul>
            </div>

            <aside className="rounded-lg border border-black/10 bg-chalk p-6" data-id="element-672" data-mp="ro1rm2329avn">
              <h2 className="font-display text-[19px] font-bold tracking-tight text-ink" data-id="element-673" data-mp="mqiex6m0h65t">
                How East End approaches it
              </h2>
              <ol className="mt-4 space-y-4" data-id="element-674" data-mp="wh4mcdjkqqdf">
                {[{
                label: 'Condition',
                copy: 'We verify what you are experiencing on the vehicle.'
              }, {
                label: 'Cause',
                copy: 'We test until the cause is confirmed rather than assumed.'
              }, {
                label: 'Correction',
                copy: 'We explain the repair options so you can decide.'
              }, {
                label: 'Confirm',
                copy: 'We verify the concern is resolved before you leave.'
              }].map((step, i) => <li key={step.label} className="flex gap-3" data-id="element-675" data-mp="3uvneqtx5lgwf" data-mpi={i}>
                    <span aria-hidden="true" className="flex h-7 w-7 shrink-0 items-center justify-center rounded bg-ink font-display text-[13px] font-bold text-brand-light" data-id="element-676" data-mp="3mq30p9yhkin">
                      {i + 1}
                    </span>
                    <div data-id="element-677" data-mp="ho2hsxddrg2y">
                      <h3 className="font-display text-[15px] font-bold tracking-tight text-ink" data-id="element-678" data-mp="hmreeynukacd">
                        {step.label}
                      </h3>
                      <p className="mt-1 text-[14px] leading-relaxed text-steel/70" data-id="element-679" data-mp="rckeb2emn10s">
                        {step.copy}
                      </p>
                    </div>
                  </li>)}
              </ol>
              <p className="mt-5 text-[13.5px] leading-relaxed text-steel/60" data-id="element-680" data-mp="10f3c383k2ng6">
                Recommendations are made after inspection and testing, not
                before.
              </p>
            </aside>
          </div>

          <h2 className="mt-16 font-display text-[24px] font-extrabold leading-[1.1] tracking-[-0.02em] text-ink sm:text-[30px]" data-id="element-681" data-mp="1fepd2y2m5f70">
            {category.name} services
          </h2>
          <ul className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-id="element-682" data-mp="15iw3nnk6y1tg">
            {category.services.map(service => <li key={service.slug} data-id="element-683" data-mp="11jdjiu071uro">
                <Link to={`/services/${category.slug}/${service.slug}`} className="group flex items-center justify-between gap-3 rounded-lg border border-black/10 bg-chalk px-4 py-3.5 text-[14.5px] leading-snug text-steel/85 transition-colors duration-150 ease-out hover:border-ink/40 hover:text-ink" data-id="element-684" data-mp="1y7jj32i9dwz3">
                  {service.name}
                  <ArrowRightIcon className="h-4 w-4 shrink-0 text-steel/40 transition-transform duration-150 ease-out group-hover:translate-x-0.5 group-hover:text-brand-dark" aria-hidden="true" data-id="element-685" data-mp="2gyb1etk84xpm" />
                </Link>
              </li>)}
          </ul>
        </div>
      </section>

      <TrustLocalBand data-id="element-686" data-mp="faokxp7t1yp1" />

      <FaqList faqs={content.faqs} heading={`${category.name} questions we hear`} data-id="element-687" data-mp="jpeokufzm8m" />

      <section className="w-full bg-ink py-14 text-white lg:py-16" data-id="element-688" data-mp="5iiw2ocunxe2">
        <div className="mx-auto flex max-w-shell flex-col gap-7 px-5 lg:flex-row lg:items-center lg:justify-between lg:px-8" data-id="element-689" data-mp="5gpkcundqmz7">
          <div data-id="element-690" data-mp="3sn8hirdxy5ri">
            <h2 className="max-w-[560px] font-display text-[26px] font-extrabold leading-[1.1] tracking-[-0.02em] sm:text-[32px]" data-id="element-691" data-mp="3snrzu97i6w62">
              Call East End about {category.name.toLowerCase()} in Oak Ridge,
              TN
            </h2>
            <p className="mt-4 max-w-[520px] text-[16px] leading-relaxed text-chalk/70" data-id="element-692" data-mp="3ipkjnsl1gi54">
              Describe what your vehicle is doing and we will tell you what
              testing or service it needs.
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center lg:shrink-0" data-id="element-693" data-mp="31fqeejo8tt0u">
            <a href={business.phoneHref} className="inline-flex items-center justify-center gap-3 whitespace-nowrap rounded-md bg-brand px-7 py-4 font-display text-lg font-bold text-white transition-colors duration-150 ease-out hover:bg-brand-light" data-id="element-694" data-mp="2wgx2gvolua2s">
              <PhoneIcon className="h-5 w-5 shrink-0" aria-hidden="true" data-id="element-695" data-mp="36d9w5tk5gnxj" />
              Call {business.phone}
            </a>
            <Link to="/services" className="inline-flex items-center justify-center whitespace-nowrap rounded-md border border-white/25 px-7 py-4 text-base font-semibold text-white transition-colors duration-150 ease-out hover:border-white/60" data-id="element-696" data-mp="3g5flevj0or91">
              All Services
            </Link>
          </div>
        </div>
      </section>
    </>;
}